/**
 * Magic Prioritization Trick - Types
 * Based on John Cutler's staged decoupling approach to surface Cost of Delay
 * 
 * The model decouples three dimensions:
 * 1. Urgency: Time sensitivity (how delay affects outcomes)
 * 2. Value: Impact magnitude (Game Changer vs Tweak)
 * 3. Duration: Time to first meaningful payoff
 */

export type UrgencyLevel = 'asap' | 'soon' | 'whenever' | null;
export type ValueLevel = 'game-changer' | 'optimizer' | 'tweak' | null;
export type DurationLevel = '1-3-months' | '1-3-quarters' | '1-3-years' | null;

export interface MagicPrioritizationItem {
  id: string;
  name: string;
  urgency: UrgencyLevel;
  value: ValueLevel;
  duration: DurationLevel;
  /** Weighted CD3 score: (urgency * value) / duration */
  weightedScore: number | null;
  /** CD3 category: high, medium, low based on combined position */
  cd3Category: 'high' | 'medium' | 'low' | null;
}

export type ScoringPhase = 'urgency' | 'value' | 'duration';

/** Numeric values for calculation */
export const URGENCY_VALUES: Record<Exclude<UrgencyLevel, null>, number> = {
  'asap': 3,
  'soon': 2,
  'whenever': 1,
};

export const VALUE_VALUES: Record<Exclude<ValueLevel, null>, number> = {
  'game-changer': 3,
  'optimizer': 2,
  'tweak': 1,
};

/** 
 * Duration is used as a DIVISOR in CD3 calculation
 * Shorter duration = smaller divisor = HIGHER final score (more valuable to do first)
 * This reflects that quick wins with same CoD should be prioritized
 */
export const DURATION_VALUES: Record<Exclude<DurationLevel, null>, number> = {
  '1-3-months': 1, // Shortest = smallest divisor = highest score
  '1-3-quarters': 2,
  '1-3-years': 3,  // Longest = largest divisor = lowest score
};

/** Labels for display */
export const URGENCY_LABELS: Record<Exclude<UrgencyLevel, null>, { label: string; description: string }> = {
  'asap': {
    label: 'ASAP',
    description: 'Time-sensitive: delay causes escalating harm or lost opportunity window',
  },
  'soon': {
    label: 'Soon',
    description: 'Important timing, but delay doesn\'t compound significantly',
  },
  'whenever': {
    label: 'Whenever',
    description: 'No significant time pressure; value remains stable over time',
  },
};

export const VALUE_LABELS: Record<Exclude<ValueLevel, null>, { label: string; description: string }> = {
  'game-changer': {
    label: 'Game Changer',
    description: 'Transforms outcomes significantly; high strategic impact',
  },
  'optimizer': {
    label: 'Optimizer',
    description: 'Meaningful improvement to existing capabilities or outcomes',
  },
  'tweak': {
    label: 'Tweak',
    description: 'Incremental benefit; small but worthwhile adjustment',
  },
};

export const DURATION_LABELS: Record<Exclude<DurationLevel, null>, { label: string; description: string }> = {
  '1-3-months': {
    label: '1-3 Months',
    description: 'Short-term: quick wins, fast feedback',
  },
  '1-3-quarters': {
    label: '1-3 Quarters',
    description: 'Medium-term: significant effort, measurable milestones',
  },
  '1-3-years': {
    label: '1-3 Years',
    description: 'Long-term: strategic investment, extended timeline',
  },
};

/**
 * Calculate weighted CD3 score
 * Formula: (Urgency × Value) / Duration
 * Higher scores = higher priority
 */
export function calculateWeightedScore(
  urgency: UrgencyLevel,
  value: ValueLevel,
  duration: DurationLevel
): number | null {
  if (!urgency || !value || !duration) return null;
  
  const urgencyValue = URGENCY_VALUES[urgency];
  const valueValue = VALUE_VALUES[value];
  const durationValue = DURATION_VALUES[duration];
  
  // CD3 = Cost of Delay / Duration
  // Cost of Delay approximated by Urgency × Value
  return (urgencyValue * valueValue) / durationValue;
}

/**
 * Determine CD3 category based on the 27-cell matrix position
 * Categories are based on weighted score thresholds
 * 
 * Score ranges (with corrected duration divisors):
 * - Max: ASAP(3) × Game Changer(3) / 1-3-months(1) = 9
 * - Min: Whenever(1) × Tweak(1) / 1-3-years(3) = 0.33
 * 
 * Thresholds designed to create roughly equal distribution across 27 cells:
 * - High (green): >= 4 - Do these first
 * - Medium (yellow): >= 1.5 and < 4 - Important but can wait
 * - Low (gray): < 1.5 - Defer or reconsider
 */
export function getCd3Category(
  urgency: UrgencyLevel,
  value: ValueLevel,
  duration: DurationLevel
): 'high' | 'medium' | 'low' | null {
  const score = calculateWeightedScore(urgency, value, duration);
  if (score === null) return null;
  
  if (score >= 4) return 'high';
  if (score >= 1.5) return 'medium';
  return 'low';
}

/**
 * Get the cell position in the 27-cell matrix
 * Returns { urgencyIndex, valueIndex, durationIndex } for visualization
 */
export function getMatrixPosition(item: MagicPrioritizationItem): {
  urgencyIndex: number;
  valueIndex: number;
  durationIndex: number;
} | null {
  if (!item.urgency || !item.value || !item.duration) return null;
  
  const urgencyOrder: Exclude<UrgencyLevel, null>[] = ['asap', 'soon', 'whenever'];
  const valueOrder: Exclude<ValueLevel, null>[] = ['game-changer', 'optimizer', 'tweak'];
  const durationOrder: Exclude<DurationLevel, null>[] = ['1-3-months', '1-3-quarters', '1-3-years'];
  
  return {
    urgencyIndex: urgencyOrder.indexOf(item.urgency),
    valueIndex: valueOrder.indexOf(item.value),
    durationIndex: durationOrder.indexOf(item.duration),
  };
}
